import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  EmptyState,
  Label,
  MoneyText,
  PageHeader,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChartWithTable } from "../../../features/fuel/shared.js";
import { useFuelAnalytics } from "../../../features/reports/queries.js";
import {
  changeTone,
  fuelHeatmapOption,
  formatPercentValue,
  heatmapRows,
  peakHours,
  productMixOption,
  rangeError,
  rangeLimitFor,
} from "../../../features/reports/report-values.js";
import {
  ReportFilterBar,
  ReportsGate,
  reportSearchShape,
  SectionCard,
  TableScroll,
  useReportFilter,
} from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDate, formatUnitPrice } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

const searchSchema = z.object(reportSearchShape);

export const Route = createFileRoute("/_app/raporlar/akaryakit")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FuelAnalyticsRoute,
});

const WINDOW_OPTIONS = [1, 2, 3, 5, 7, 14] as const;
/** Tabanca tablosu ilk açılışta en çok satan 20 tabancayı gösterir (zincirde yüzlerce satır olabilir). */
const NOZZLE_PREVIEW = 20;

/**
 * Akaryakıt analitiği (Prompt 22, ADR 0022 §9): yoğun saat ısı haritası (hafta günü × saat, ortalama litre / saat), tabanca
 * başına hacim, ürün karışımı ve fiyat değişikliklerinin öncesi / sonrası günlük ortalama hacmi. Dönem en çok 92 gün (saatlik
 * veriden hesaplanır).
 */
function FuelAnalyticsRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsFuel"));
  return (
    <>
      <PageHeader title={t("menu.reportsFuel")} description={t("reports.fuel.description")} />
      <ReportsGate>
        <FuelAnalytics />
      </ReportsGate>
    </>
  );
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function FuelAnalytics(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [state, setState, explicit] = useReportFilter(search, "LAST_30_DAYS");
  const windowId = useId();
  const maxDays = rangeLimitFor("FUEL_ANALYTICS");
  const valid = rangeError(state.range, maxDays) === null;
  const analytics = useFuelAnalytics(
    state.range,
    { companyId: state.companyId, stationId: state.stationId ?? null, windowDays: state.windowDays ?? 3 },
    valid,
  );
  const data = analytics.data;
  const peaks = data ? peakHours(data.heatmap) : [];
  const [allNozzles, setAllNozzles] = useState(false);
  const nozzles = data ? (allNozzles ? data.nozzles : data.nozzles.slice(0, NOZZLE_PREVIEW)) : [];

  return (
    <div className="grid gap-6">
      <ReportFilterBar
        reportKey="FUEL_ANALYTICS"
        state={state}
        onChange={setState}
        stations="single"
        maxDays={maxDays}
        explicit={explicit}
        extra={
          <div className="grid gap-1.5">
            <Label htmlFor={windowId}>{t("reports.fuel.window")}</Label>
            <Select
              id={windowId}
              className="w-52"
              value={String(state.windowDays ?? 3)}
              onChange={(event) => setState({ ...state, windowDays: Number(event.target.value) })}
            >
              {WINDOW_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {t("reports.fuel.windowDays", { count: days })}
                </option>
              ))}
            </Select>
          </div>
        }
      />
      {analytics.error ? <Alert tone="danger">{errorMessage(analytics.error)}</Alert> : null}
      {!data ? (
        analytics.isPending && valid ? (
          <SkeletonRows rows={6} />
        ) : null
      ) : data.nozzles.length === 0 && data.productMix.length === 0 ? (
        <EmptyState title={t("reports.fuel.emptyTitle")} description={t("reports.fuel.emptyDescription")} />
      ) : (
        <>
          <SectionCard
            title={t("reports.fuel.heatmapTitle")}
            description={
              peaks.length > 0
                ? t("reports.fuel.peaks", {
                    list: peaks
                      .map(
                        (peak) =>
                          `${peak.weekday} ${peak.hour} (${Decimal.from(peak.averageLitres).formatTr(0)} L)`,
                      )
                      .join(", "),
                  })
                : t("reports.fuel.heatmapDescription")
            }
          >
            <ChartWithTable
              ariaLabel={t("reports.fuel.heatmapAria")}
              option={fuelHeatmapOption(data.heatmap, t("reports.fuel.averageLitres"))}
              height={340}
              tableLabel={t("reports.fuel.heatmapTable")}
            >
              <table className="w-full min-w-[1200px] text-xs">
                <caption className="sr-only">{t("reports.fuel.heatmapTable")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t("reports.fuel.weekday")}</Th>
                    {Array.from({ length: 24 }, (_unused, hour) => (
                      <Th key={hour} align="right">
                        {String(hour).padStart(2, "0")}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapRows(data.heatmap).map((row) => (
                    <tr key={row.weekday} className="border-t">
                      <th scope="row" className="px-3 py-1.5 text-left font-medium">
                        {row.label}
                      </th>
                      {row.hours.map((value, hour) => (
                        <td key={hour} className="tabular px-2 py-1.5 text-right">
                          {Decimal.from(value).formatTr(0)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartWithTable>
          </SectionCard>

          <div className="grid gap-6 xl:grid-cols-2">
            <SectionCard title={t("reports.fuel.mixTitle")} description={t("reports.fuel.mixDescription")}>
              <ChartWithTable
                ariaLabel={t("reports.fuel.mixAria")}
                option={productMixOption(data.productMix, t("reports.metrics.fuelLitres"))}
                height={Math.max(200, data.productMix.length * 40 + 60)}
                tableLabel={t("reports.fuel.mixTable")}
              >
                <table className="w-full text-sm">
                  <caption className="sr-only">{t("reports.fuel.mixTable")}</caption>
                  <thead className="bg-muted/50">
                    <tr>
                      <Th>{t("reports.columns.product")}</Th>
                      <Th align="right">{t("reports.metrics.fuelLitres")}</Th>
                      <Th align="right">{t("reports.columns.amount")}</Th>
                      <Th align="right">{t("reports.columns.share")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.productMix.map((row) => (
                      <tr key={row.productId} className="border-t">
                        <td className="px-3 py-2">{row.productName}</td>
                        <td className="px-3 py-2 text-right">
                          <QuantityText value={row.litres} unit="LITRE" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <MoneyText value={row.amount} />
                        </td>
                        <td className="tabular px-3 py-2 text-right">{formatPercentValue(row.share)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ChartWithTable>
            </SectionCard>

            <SectionCard
              title={t("reports.fuel.priceTitle")}
              description={t("reports.fuel.priceDescription", { count: data.windowDays })}
            >
              {data.priceChanges.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("reports.fuel.noPriceChanges")}</p>
              ) : (
                <TableScroll label={t("reports.fuel.priceTitle")}>
                  <table className="w-full min-w-[640px] text-sm">
                    <caption className="sr-only">{t("reports.fuel.priceTitle")}</caption>
                    <thead className="bg-muted/50">
                      <tr>
                        <Th>{t("reports.columns.date")}</Th>
                        <Th>{t("reports.columns.stationProduct")}</Th>
                        <Th align="right">{t("reports.columns.price")}</Th>
                        <Th align="right">{t("reports.columns.beforeAfter")}</Th>
                        <Th align="right">{t("reports.columns.volumeChange")}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.priceChanges.map((row) => (
                        <tr key={row.id} className="border-t">
                          <td className="px-3 py-2">{formatDate(row.effectiveDate)}</td>
                          <td className="px-3 py-2">
                            {row.stationCode} · {row.productName}
                          </td>
                          <td className="tabular px-3 py-2 text-right">
                            {row.oldPrice ? `${formatUnitPrice(row.oldPrice)} → ` : ""}
                            {formatUnitPrice(row.newPrice)}{" "}
                            <span className="text-xs text-muted-foreground">
                              ({formatPercentValue(row.priceChangePercent)})
                            </span>
                          </td>
                          <td className="tabular px-3 py-2 text-right">
                            <QuantityText value={row.beforeAverageLitres} unit="LITRE" /> →{" "}
                            <QuantityText value={row.afterAverageLitres} unit="LITRE" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <StatusBadge tone={changeTone(row.volumeChangePercent)}>
                              {formatPercentValue(row.volumeChangePercent)}
                            </StatusBadge>
                            {row.partial ? (
                              <span className="ml-1 text-xs text-muted-foreground">
                                {t("reports.fuel.partial")}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </SectionCard>
          </div>

          <SectionCard
            title={t("reports.fuel.nozzlesTitle")}
            description={t("reports.fuel.nozzlesDescription")}
          >
            <TableScroll label={t("reports.fuel.nozzlesTitle")}>
              <table className="w-full min-w-[720px] text-sm">
                <caption className="sr-only">{t("reports.fuel.nozzlesTitle")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t("reports.columns.station")}</Th>
                    <Th>{t("reports.columns.nozzle")}</Th>
                    <Th>{t("reports.columns.product")}</Th>
                    <Th align="right">{t("reports.metrics.fuelLitres")}</Th>
                    <Th align="right">{t("reports.columns.amount")}</Th>
                    <Th align="right">{t("reports.columns.fuelTransactions")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {nozzles.map((row) => (
                    <tr key={row.nozzleId} className="border-t">
                      <td className="px-3 py-2">{row.stationCode}</td>
                      <td className="whitespace-nowrap px-3 py-2">{row.label}</td>
                      <td className="px-3 py-2">{row.productName}</td>
                      <td className="px-3 py-2 text-right">
                        <QuantityText value={row.litres} unit="LITRE" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.amount} />
                      </td>
                      <td className="tabular px-3 py-2 text-right">{formatCount(row.transactions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            {data.nozzles.length > NOZZLE_PREVIEW ? (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-muted-foreground">
                  {t("reports.fuel.nozzlesShown", { shown: nozzles.length, total: data.nozzles.length })}
                </span>
                <Button variant="outline" size="sm" onClick={() => setAllNozzles(!allNozzles)}>
                  {allNozzles ? t("reports.fuel.nozzlesLess") : t("reports.fuel.nozzlesAll")}
                </Button>
              </div>
            ) : null}
          </SectionCard>
        </>
      )}
    </div>
  );
}
